import { action, computed } from 'mobx';
import { isNumber } from 'lodash';
import Validator from 'validatorjs';

import FieldState from './FieldState';
import ArrayFieldState from './ArrayFieldState';

/**
 * A variation of an ArrayFieldState that represents an Array of primitives rolled up as a single field
 */
class FlatArrayFieldState extends ArrayFieldState {
  @action
  add(value, options = {}) {
    const { validate = true, suppressOnChange = false } = options;
    // don't pass the helpText down to each field state
    const { helpText, ...initialConfig } = this.initialConfig;
    this.fieldStates.push(new FieldState(this.form, { ...initialConfig, value, parent: this }));
    if (validate) {
      this.form.validate();
    }

    if (!suppressOnChange && this.form.onChange) {
      this.form.onChange({
        form: this.form,
        formValues: this.form.getValues(),
        field: this,
        fieldValue: value
      });
    }
  }

  @action
  remove(idxOrField) {
    let idx = idxOrField;

    if (!isNumber(idxOrField)) {
      idx = this.fieldStates.findIndex((field) => field === idxOrField);
    }

    super.remove(idx);
  }

  @action
  clearValue() {
    this.fieldStates.clear();
    this.form.validate();
  }

  @action
  setChildren(children) {
    this.children = children;
    this.form.validate();
  }

  @action
  validate(values) {
    FieldState.prototype.validate.call(this, values);

    this.fieldStates.forEach((field, rowIdx) => {
      const rowValue = this.getValue(rowIdx);

      if (this.ignore(rowValue)) {
        field.errors = [];
        return;
      }

      const value = { ...values };
      value[field.name] = rowValue;

      const rules = {
        [field.name]: this.children ? this.children.rules : this.rules
      };

      const attributeNames = {
        [this.name]: this.label || this.name
      };

      const messages = this.children ? this.children.messages : {};

      const validator = new Validator(value, rules, messages);
      validator.setAttributeNames(attributeNames);
      validator.check();

      field.errors = validator.errors.get(field.name);

      if (field.errors && field.errors.length > 0) {
        this.errors.push(`${this.label} (${rowIdx + 1}): ${field.errors}`);
      }
    });
  }

  getValue(rowIdx) {
    if (rowIdx >= 0) {
      const rowValue = this.fieldStates[rowIdx].getValue();

      return this.exclude(rowValue) ? undefined : rowValue;
    }

    return super.getValue();
  }

  @computed
  get pristine() {
    return this.fieldStates.every((field) => field.pristine);
  }

  @computed
  get hasError() {
    return (this.errors && this.errors.length > 0) || this.fieldStates.some((row) => row.hasError);
  }
}

export default FlatArrayFieldState;
